import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { LinearGradient } from "react-text-gradients";
import {
  IoClose,
  IoDiamond,
  IoPeople,
  IoWallet,
  IoCheckmarkCircle,
  IoFlash,
  IoCash,
  IoTime,
  IoTrophy,
  IoGift,
} from "react-icons/io5";

const MODAL_HEIGHT = "55vh";

// Animation keyframes
const slideUp = keyframes`
  from { transform: translateY(${MODAL_HEIGHT}); }
  to { transform: translateY(0); }
`;

const slideDown = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(${MODAL_HEIGHT}); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
`;

const shine = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
align-items: flex-end;
  z-index: 1000;
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  animation: ${fadeIn} 0.3s forwards;

  &.closing {
    animation: ${fadeOut} 0.25s forwards;
  }
`;

const ModalContainer = styled.div`
  height: ${MODAL_HEIGHT};
  background-color: white;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  overflow: hidden;
  box-shadow: 0 -5px 8px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.4s forwards;
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;

  &.closing {
    animation: ${slideDown} 0.3s forwards;
  }
`;

const ModalHeader = styled.div`
  padding: 12px 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 35%;
  background: linear-gradient(
    to right bottom,
    var(--gradient-start),
    var(--gradient-end)
  );
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 1;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  color: white;
`;

const IconContainer = styled.div`
  margin: 8px 0 6px;
  padding: 8px;
  border-radius: 32px;
  background-color: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.15);

  &.spin {
    animation: ${spin} 3s linear infinite;
  }

  &.pulse {
    animation: ${pulse} 2s ease-in-out infinite;
  }

  &.bounce {
    animation: ${bounce} 1.2s ease-in-out infinite;
  }

  &.shake {
    animation: ${shake} 0.3s ease-in-out infinite;
  }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: white;
  text-align: center;
  margin-bottom: 2px;
  line-height: 22px;
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  font-weight: 500;
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const BenefitContainer = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 8px 0;
  background-color: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 8px;
`;

const BenefitItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const BenefitText = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #333;
  margin-top: 3px;
  text-align: center;
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 0;
`;

const Description = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin-bottom: 6px;
  line-height: 22px;
`;

const SubText = styled.p`
  font-size: 13px;
  color: #666;
  text-align: center;
  line-height: 18px;
  margin-bottom: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  margin-top: 8px;
`;

const ActionButtonWrapper = styled.div`
  flex: 1;
  border-radius: 8px;
  overflow: hidden;
`;

const ActionButton = styled.button`
  height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  box-shadow: 0 3px 4px rgba(0, 0, 0, 0.2);
  width: 100%;
  background: linear-gradient(
    to right,
    var(--gradient-start),
    var(--gradient-end)
  );

  &.light {
    border: 1px solid #d2691e;
    background: linear-gradient(to right, #ffecd2, #fcb69f);
  }
`;

const ButtonShine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 50%;
  animation: ${shine} 1.8s linear infinite;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.8),
    transparent
  );
`;

const ButtonText = styled.span`
  color: ${(props) => (props.light ? "#8B4513" : "white")};
  font-size: 13px;
  font-weight: bold;
  display: flex;
  align-items: center;
`;

const ButtonIcon = styled.span`
  margin-right: 4px;
  display: flex;
  align-items: center;
`;

const ImageIcon = styled.img`
  width: 15px;
  height: 15px;
  object-fit: contain;
`;

export const StakeModal = ({ onClose }) => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      onClose();
    }, 300);
  };

  const handleStakeNow = () => {
    handleClose(); 
    navigate("/home/stake-points");
  };

  if (!visible) return null;

  return (
    <ModalOverlay>
      <Backdrop className={closing ? "closing" : ""} onClick={handleClose} />
      <ModalContainer
        className={closing ? "closing" : ""}
        style={{ "--gradient-start": "#667eea", "--gradient-end": "#764ba2" }}
      >
        <ModalHeader>
          <CloseButton onClick={handleClose}>
            <IoClose size={18} />
          </CloseButton>
          <IconContainer className="spin">
            <IoDiamond size={28} color="#FFD700" />
          </IconContainer>
          <ModalTitle>💎 Stake & Earn</ModalTitle>
          <Subtitle>Grow your wealth daily</Subtitle>
        </ModalHeader>
        <ModalBody>
          <BenefitContainer>
            <BenefitItem>
              <ImageIcon src="https://res.cloudinary.com/dztzqhifb/image/upload/v1747804874/ny3f7rmazixfowkcw1ig.png" />
              <BenefitText>Earn Up to 52% Returns!</BenefitText>
            </BenefitItem>
            <BenefitItem>
              <IoTime size={16} color="#2196F3" />
              <BenefitText>Daily Rewards</BenefitText>
            </BenefitItem>
          </BenefitContainer>
          <ContentContainer>
            <Description>
              🚀 Stake Your Points & Earn Daily Rewards Automatically
            </Description>
            <SubText>
              Grow your points exponentially with our staking program and earn
              up to 52% annual returns with daily compounding.
            </SubText>
          </ContentContainer>
          <ButtonContainer>
            <ActionButtonWrapper>
              <ActionButton onClick={handleStakeNow}>
                <ButtonShine />
                <ButtonText>
                  <ButtonIcon>
                    <IoDiamond size={14} />
                  </ButtonIcon>
                  Stake Now
                </ButtonText>
              </ActionButton>
            </ActionButtonWrapper>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export const ReferralModal = ({ onClose }) => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      onClose();
    }, 300);
  };

  const handleBuyNow = () => {
    handleClose();
    navigate("/referral");
  };

  if (!visible) return null;

  return (
    <ModalOverlay>
      <Backdrop className={closing ? "closing" : ""} onClick={handleClose} />
      <ModalContainer
        className={closing ? "closing" : ""}
        style={{ "--gradient-start": "#11998e", "--gradient-end": "#38ef7d" }}
      >
        <ModalHeader>
          <CloseButton onClick={handleClose}>
            <IoClose size={18} />
          </CloseButton>
          <IconContainer className="pulse">
            <IoPeople size={28} color="#4169E1" />
          </IconContainer>
          <ModalTitle>👥 Build Your Team</ModalTitle>
          <Subtitle>50% cashback guaranteed</Subtitle>
        </ModalHeader>
        <ModalBody>
          <BenefitContainer>
            <BenefitItem>
              <IoCash size={16} color="#4CAF50" />
              <BenefitText>50% Cashback Points</BenefitText>
            </BenefitItem>
            <BenefitItem>
              <IoFlash size={16} color="#FF9800" />
              <BenefitText>Instant Activation</BenefitText>
            </BenefitItem>
          </BenefitContainer>
          <ContentContainer>
            <Description>
              🎯 Build Your Team & Earn 50% Cashback on Every Purchase
            </Description>
            <SubText>
              Get active referrals instantly and maximize your earnings through
              our team bonus program.
            </SubText>
          </ContentContainer>
          <ButtonContainer>
            <ActionButtonWrapper>
              <ActionButton onClick={handleBuyNow}>
                <ButtonShine />
                <ButtonText>
                  <ButtonIcon>
                    <IoPeople size={14} />
                  </ButtonIcon>
                  Buy Now
                </ButtonText>
              </ActionButton>
            </ActionButtonWrapper>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export const PointsModal = ({ onClose }) => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      onClose();
    }, 300);
  };

  const handleBuyNow = () => {
    handleClose(); 
    navigate("/home/deposit-method");
  };

  if (!visible) return null;

  return (
    <ModalOverlay>
      <Backdrop className={closing ? "closing" : ""} onClick={handleClose} />
      <ModalContainer
        className={closing ? "closing" : ""}
        style={{ "--gradient-start": "#ffecd2", "--gradient-end": "#fcb69f" }}
      >
        <ModalHeader>
          <CloseButton onClick={handleClose}>
            <IoClose size={18} color="#8B4513" />
          </CloseButton>
          <IconContainer className="bounce">
            <IoWallet size={28} color="#32CD32" />
          </IconContainer>
          <ModalTitle style={{ color: "#8B4513" }}>💰 Buy Points</ModalTitle>
          <Subtitle style={{ color: "#A0522D" }}>
            Instant rewards unlock
          </Subtitle>
        </ModalHeader>
        <ModalBody>
          <BenefitContainer>
            <BenefitItem>
              <IoFlash size={16} color="#FF9800" />
              <BenefitText>Instant Purchase</BenefitText>
            </BenefitItem>
            <BenefitItem>
              <IoGift size={16} color="#E91E63" />
              <BenefitText>Bonus Rewards</BenefitText>
            </BenefitItem>
          </BenefitContainer>
          <ContentContainer>
            <Description>⚡ Buy Points Instantly & Unlock Rewards</Description>
            <SubText>
              Get immediate access to exclusive features and special bonuses
              with our point packages.
            </SubText>
          </ContentContainer>
          <ButtonContainer>
            <ActionButtonWrapper>
              <ActionButton className="light" onClick={handleBuyNow}>
                <ButtonShine />
                <ButtonText light>
                  <ButtonIcon>
                    <IoWallet size={14} color="#8B4513" />
                  </ButtonIcon>
                  Buy Now
                </ButtonText>
              </ActionButton>
            </ActionButtonWrapper>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export const TasksModal = ({ onClose }) => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      onClose();
    }, 300);
  };

  const handleStartNow = () => {
    handleClose();
    navigate("/home");
  };

  if (!visible) return null;

  return (
    <ModalOverlay>
      <Backdrop className={closing ? "closing" : ""} onClick={handleClose} />
      <ModalContainer
        className={closing ? "closing" : ""}
        style={{ "--gradient-start": "#8A2387", "--gradient-end": "#E94057" }}
      >
        <ModalHeader>
          <CloseButton onClick={handleClose}>
            <IoClose size={18} />
          </CloseButton>
          <IconContainer className="shake">
            <IoCheckmarkCircle size={28} color="#9932CC" />
          </IconContainer>
          <ModalTitle>🎯 Complete Tasks</ModalTitle>
          <Subtitle>Earn big rewards</Subtitle>
        </ModalHeader>
        <ModalBody>
          <BenefitContainer>
            <BenefitItem>
              <IoTrophy size={16} color="#FFD700" />
              <BenefitText>10M Points</BenefitText>
            </BenefitItem>
            <BenefitItem>
              <ImageIcon src="https://res.cloudinary.com/dztzqhifb/image/upload/v1747804874/ny3f7rmazixfowkcw1ig.png" />
              <BenefitText>Rank Boost</BenefitText>
            </BenefitItem>
          </BenefitContainer>
          <ContentContainer>
            <Description>
              🏆 Complete Simple Tasks & Earn Up To 10,00,000 Points
            </Description>
            <SubText>
              Boost your leaderboard rank and maximize your earnings by
              completing these quick and easy tasks daily.
            </SubText>
          </ContentContainer>
          <div style={{ height: "5px" }} />
          <ButtonContainer>
            <ActionButtonWrapper>
              <ActionButton onClick={handleStartNow}>
                <ButtonShine />
                <ButtonText>
                  <ButtonIcon>
                    <IoCheckmarkCircle size={14} />
                  </ButtonIcon>
                  Start Now
                </ButtonText>
              </ActionButton>
            </ActionButtonWrapper>
          </ButtonContainer>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export const RandomModalWrapper = () => {
  const [selectedModal, setSelectedModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * 4);
    setSelectedModal(randomIndex);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedModal(null);
  };

  const renderModal = () => {
    switch (selectedModal) {
      case 0:
        return <StakeModal onClose={handleModalClose} />;
      case 1:
        return <ReferralModal onClose={handleModalClose} />;
      case 2:
        return <PointsModal onClose={handleModalClose} />;
      case 3:
        return <TasksModal onClose={handleModalClose} />;
      default:
        return null;
    }
  };

  if (!isModalOpen || selectedModal === null) {
    return null;
  }
  return renderModal();
};

export default RandomModalWrapper;
